import { z } from 'zod';

export const EntityAppliedInSchema = z.enum(['client', 'ticket', 'equipment']);
export type EntityAppliedIn = z.infer<typeof EntityAppliedInSchema>;

export const EntityFieldTypeSchema = z.enum([
  'text',
  'text_area',
  'currency',
  'date',
  'phone',
  'email',
  'single_select',
  'checkbox',
  'link',
]);
export type EntityFieldType = z.infer<typeof EntityFieldTypeSchema>;

export const EntityFieldOptionSchema = z.object({
  id: z.number().int(),
  fieldId: z.number().int(),
  value: z.string(),
});
export type EntityFieldOption = z.infer<typeof EntityFieldOptionSchema>;

export const EntityFieldSchema = z.object({
  id: z.number().int(),
  groupId: z.number().int(),
  name: z.string(),
  fieldType: EntityFieldTypeSchema,
  required: z.boolean(),
  position: z.number().int(),
  options: z.array(EntityFieldOptionSchema),
});
export type EntityField = z.infer<typeof EntityFieldSchema>;

export const EntityGroupSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  appliedIn: EntityAppliedInSchema,
  active: z.boolean(),
  fields: z.array(EntityFieldSchema),
});
export type EntityGroup = z.infer<typeof EntityGroupSchema>;

/**
 * Item do GET /entities (tela /settings/entities, v7): a tabela mostra
 * Nome | Mesas | Quantidade de campos | Status. `deskNames` vazio = grupo vale
 * para TODAS as mesas (sem vínculo em entity_group_desks); só grupos de
 * applied_in = ticket podem ter vínculo.
 */
export const EntityGroupListItemSchema = EntityGroupSchema.extend({
  deskNames: z.array(z.string()),
  /** mesmos vínculos, por id (mesma ordem de deskNames) — o web pré-seleciona por id */
  deskIds: z.array(z.number().int()),
  fieldCount: z.number().int(),
});
export type EntityGroupListItem = z.infer<typeof EntityGroupListItemSchema>;

export const EntityGroupBodySchema = z.object({
  name: z.string().min(1),
  appliedIn: EntityAppliedInSchema,
  /** só para applied_in = ticket; substitui os vínculos; vazio/ausente mantém "todas as mesas" */
  deskIds: z.array(z.number().int().positive()).optional(),
});

export const EntityFieldBodySchema = z.object({
  name: z.string().min(1),
  fieldType: EntityFieldTypeSchema,
  required: z.boolean().default(false),
  options: z.array(z.string().min(1)).optional(),
});
export type EntityFieldBody = z.infer<typeof EntityFieldBodySchema>;

/** Filled value as returned inside client/ticket detail. */
export const EntityValueSchema = z.object({
  fieldId: z.number().int(),
  groupId: z.number().int(),
  groupName: z.string(),
  fieldName: z.string(),
  fieldType: EntityFieldTypeSchema,
  required: z.boolean(),
  value: z.string().nullable(),
  optionId: z.number().int().nullable(),
});
export type EntityValue = z.infer<typeof EntityValueSchema>;

export const EntityValuesUpdateBodySchema = z.object({
  values: z.array(
    z.object({
      fieldId: z.number().int().positive(),
      value: z.string().nullable(),
      optionId: z.number().int().positive().nullish(),
    })
  ),
});
export type EntityValuesUpdateBody = z.infer<typeof EntityValuesUpdateBodySchema>;
